import {
  Button,
  Checkbox,
  DatePicker,
  DatePickerProps,
  Form,
  FormProps,
  Input,
  Radio,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import ZikZakTik from "../../../assets/icons/ZikZakTik";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  createEventAsync,
  singEventDataAsync,
  updateEventAsync,
} from "../../../store/features/EventManage/Event/eventApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { EventData } from "../../../types/eventManage/event";
import CustomSection from "../../ui/CustomSection";
import { DimensionOfImage } from "../../ui/DimensionOfImage";
import FileUpload from "../../ui/FileUpload";
import IconButton from "../../ui/IconButton";
import IconicTitle from "../../ui/IconicTitle";

export default function AddEvents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [checkAll, setCheckAll] = useState(false);

  const { manageLoading, singleEventData } = useAppSelector(
    (state) => state.event
  );

  const { login } = useAppSelector((state) => state.login);

  const createEventData = singleEventData as EventData;

  const statusList = [
    "Published",
    "CSO-Admin-Approved",
    "Draft",
    "Pending",
    "Cancel",
  ];

  const filteredStatusList =
    login?.role_id?.role_type === "cso_admin"
      ? statusList.filter((item) => item !== "Published")
      : statusList.filter((item) => item !== "CSO-Admin-Approved");

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const { eventStartDate, eventEndDate, ...rest } = values;
    const newValue = {
      ...rest,
      eventStartDate: eventStartDate ? eventStartDate.valueOf() : null, // This will include time
      eventEndDate: eventEndDate ? eventEndDate.valueOf() : null, // This will include time
    };

    if (id) {
      dispatch(
        updateEventAsync({
          data: newValue,
          others: {
            reRender,
            id,
          },
        })
      );
    } else {
      dispatch(
        createEventAsync({
          data: newValue,
          others: {
            reRender,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(
        singEventDataAsync({
          params: {
            id,
            reRender(val) {},
          },
        })
      );
    }
  }, [dispatch, id]);

  // add value in modal for view and edit
  useEffect(() => {
    if (id && createEventData?._id && manageLoading === false) {
      form.setFieldsValue({
        eventTitle: createEventData?.eventTitle,
        eventStartDate: createEventData?.eventStartDate
          ? moment(createEventData?.eventStartDate)
          : null,
        eventEndDate: createEventData?.eventEndDate
          ? moment(createEventData?.eventEndDate)
          : null,
        eventOrganizedBy: createEventData?.eventOrganizedBy,
        eventHostBy: createEventData?.eventHostBy,
        eventVenueAddress: createEventData?.eventVenueAddress,
        eventGuests: createEventData?.eventGuests?.map((guest) => ({
          fullName: guest.fullName,
          profession: guest.profession,
          designation: guest.designation,
          photo: guest.photo,
        })),
        eventStatus: createEventData?.eventStatus,
      });
    }
  }, [
    createEventData?._id,
    createEventData?.eventEndDate,
    createEventData?.eventGuests,
    createEventData?.eventHostBy,
    createEventData?.eventOrganizedBy,
    createEventData?.eventStartDate,
    createEventData?.eventStatus,
    createEventData?.eventTitle,
    createEventData?.eventVenueAddress,
    form,
    id,
    manageLoading,
  ]);

  function reRender(val: boolean) {
    if (val) {
      navigate("/admin/event-manage/events");
    }
  }

  const handleDateChange: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {};

  const handleCheckAll = (e: CheckboxChangeEvent) => {
    setCheckAll(e.target.checked);
  };

  return (
    <AdminLayout
      title={`${id ? "Event Update" : "Create a New Event"}`}
      backLink="/admin/event-manage/events"
    >
      <Form
        className="custom-form space-y-[14px]"
        layout="vertical"
        onFinish={onFinish}
        size="large"
        form={form}
      >
        <CustomSection>
          <div className="grid grid-cols-2 gap-x-2.5 w-full">
            <Form.Item
              name="eventTitle"
              label="Event Title"
              rules={[
                {
                  required: true,
                  message: "Please select title",
                },
              ]}
            >
              <Input placeholder="Enter event title" size="large" />
            </Form.Item>

            <Form.Item
              name="eventStartDate"
              label="Event Start Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                onChange={handleDateChange}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="eventEndDate"
              label="Event End Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                onChange={handleDateChange}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="eventOrganizedBy"
              label="Organized By"
              rules={[
                {
                  required: true,
                  message: "Please organization name",
                },
              ]}
            >
              <Input placeholder="Enter organization name" size="large" />
            </Form.Item>

            <Form.Item
              name="eventHostBy"
              label="Host By"
              rules={[
                {
                  required: true,
                  message: "Please enter host name",
                },
              ]}
            >
              <Input placeholder="Enter host name" size="large" />
            </Form.Item>
            <Form.Item
              // className="col-span-2"
              name="eventVenueAddress"
              label="Event Venue Address"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Input placeholder="Address" size="large" />
            </Form.Item>
          </div>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={<ZikZakTik />}
            className="text-primary"
            title="Add Event's Guests"
          />

          <Form.List initialValue={[undefined]} name="eventGuests">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map((field, index) => (
                    <div className="relative" key={field.key}>
                      <div className="grid grid-cols-2 gap-x-2.5 w-full mt-4">
                        <Form.Item
                          name={[index, "fullName"]}
                          label="Guest Full Name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter name",
                            },
                          ]}
                        >
                          <Input placeholder="Enter full name" size="large" />
                        </Form.Item>

                        <Form.Item
                          name={[index, "profession"]}
                          label="Guest Profession"
                          rules={[
                            {
                              required: true,
                              message: "Please enter profession",
                            },
                          ]}
                        >
                          <Input
                            placeholder="e. g Senior Journalism"
                            size="large"
                          />
                        </Form.Item>

                        <Form.Item
                          name={[index, "designation"]}
                          label="Guest Designation"
                          rules={[
                            {
                              required: true,
                              message: "Please enter designation",
                            },
                          ]}
                        >
                          <Input placeholder="e. g Chief Guest" size="large" />
                        </Form.Item>

                        <Form.Item
                          name={[index, "photo"]}
                          label="Guest Photo"
                          valuePropName="fileUrl"
                          extra={DimensionOfImage({ width: 74, height: 93 })}
                          rules={[
                            {
                              required: false,
                              message: "Please upload photo",
                            },
                          ]}
                        >
                          <FileUpload
                            form={form}
                            accept={acceptFile}
                            name="photo"
                            onChange={(url) =>
                              form.setFieldsValue({ photo: url || undefined })
                            }
                          />
                        </Form.Item>
                      </div>

                      {fields.length > 1 ? (
                        <div className="border border-solid border-[#AFBACA] mt-1 mb-4 w-full" />
                      ) : null}

                      {fields.length > 1 ? (
                        <div className="flex justify-end mt-1 absolute right-0 -top-0">
                          <button
                            className="rounded-full bg-transparent border-none cursor-pointer"
                            onClick={() => remove(field.name)}
                          >
                            <span className="relative top-[1px]">
                              <DeleteIcon />
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}

                  <div
                    className={`flex items-end mb-3 ${
                      fields?.length % 2 === 0 ? "mt-6" : "mt-2"
                    }`}
                  >
                    <div className="flex justify-end ">
                      <IconButton
                        className="!text-sm !font-normal !rounded-lg !text-primary !bg-[#FFF5F4]"
                        type="text"
                        icon={<AddIcon color="#dc3545 " />}
                        onClick={() => add()}
                      >
                        <span>Add More</span>
                      </IconButton>
                    </div>
                  </div>
                </>
              );
            }}
          </Form.List>
        </CustomSection>

        <CustomSection>
          <div className="flex items-center  gap-x-2.5 mb-4">
            <Form.Item
              name="eventStatus"
              label="Event Status"
              rules={[
                {
                  required: true,
                  message: "Please select event status",
                },
              ]}
            >
              <Radio.Group className="custom-radio" size="large">
                {filteredStatusList?.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="border border-solid border-[#d7dee8] mt-1 mb-4 w-full" />

          <div className="flex justify-between items-center">
            <Checkbox checked={checkAll} onChange={handleCheckAll}>
              <span className="text-body text-sm font-medium leading-normal">
                Yes! I confirmed and Publish this event
              </span>
            </Checkbox>

            <Button
              type="primary"
              className="primary_btn"
              htmlType="submit"
              disabled={
                !form.isFieldsTouched(true) ||
                !!form.getFieldsError().filter(({ errors }) => errors.length)
                  .length ||
                !checkAll
              }
            >
              {id ? "Update" : "Save"}
            </Button>
          </div>
        </CustomSection>
      </Form>
    </AdminLayout>
  );
}

const acceptFile = ".jpg, .jpeg, .png";
